import { ALL_THEMES } from 'lib/context/ThemeContext';
import { tv } from 'tailwind-variants';

const themeVariants = ALL_THEMES.reduce(
  (acc, curr) => ((acc[curr] = {}), acc),
  {} as Record<string, object>
);

export const cardSVG = tv({
  base: [
    'absolute',
    'duration-400',
    'ease-in',
    'group-hover:duration-500',
    'group-hover:transition',
    'group-hover:translate-y-[2%]',
    '-top-[2%]',
    'transition-transform',
    'transition',
    'translate-y-[0]',
    'w-full',
    '[&_svg]:h-auto',
    '[&_svg]:w-full',
  ],
  variants: {
    brand: {
      ...themeVariants,
      Off: [
        'left-1/2',
        '-translate-x-[50%]',
        'lg:bottom-[50%]',
        'md:bottom-[58%]',
        'md:-ml-[14px]',
        'md:-mt-[8px]',
        'md:w-[635px]',
      ],
      Autan: [
        'left-1/2',
        '-translate-x-[50%]',
        'lg:bottom-[50%]',
        'md:bottom-[58%]',
        'md:-ml-[5px]',
        'md:-mt-[8px]',
        'md:w-[635px]',
      ],
      AutanDefense: [],
      Ziploc: [
        'right-0',
        '!w-[110%]',
        '[&_svg]:md:h-auto',
        '[&_svg]:md:w-auto',
        'md:left-[-193px]',
        'md:top-[-16px]',
      ],
      Baygon: ['!w-[110%]', 'left-[-14px]', 'md:left-[-30px]'],
      Pyrel: [],
      Lysoform: ['top-0'],
      Drano: ['top-[3%]'],
      ScrubbingBubbles: ['!top-[14%]'],
    },
    size: {
      large: ['hidden', 'md:block'],
      small: ['block', 'md:hidden'],
    },
    grid: { '2column': [], '3column': [], '4column': [] },
  },
  compoundVariants: [
    {
      brand: 'Ziploc',
      grid: '2column',
      size: 'large',
      className: ['md:left-auto'],
    },
    {
      brand: 'Off',
      grid: '2column',
      size: 'large',
      className: ['lg:bottom-auto', 'lg:-ml-[0px]', 'lg:top-0', 'lg:w-[165%]', 'md:bottom-[52%]'],
    },
    {
      brand: 'Off',
      grid: '4column',
      size: 'large',
      className: ['lg:bottom-[55%]', 'md:bottom-[63%]'],
    },
    { brand: 'Baygon', grid: '4column', size: 'large', className: ['-top-[1%]'] },
    {
      brand: 'Baygon',
      grid: '2column',
      size: 'large',
      className: ['!-top-[10%]', '!left-[-40px]', '!w-full'],
    },
    {
      brand: 'Pyrel',
      grid: '2column',
      size: 'large',
      className: ['top-[-30%]'],
    },
    { brand: 'Lysoform', grid: '2column', size: 'large', className: ['w-[120%]', '-top-[25%]'] },
    { brand: 'Drano', grid: '2column', size: 'large', className: ['!top-[12%]'] },
    { brand: 'ScrubbingBubbles', grid: '2column', size: 'large', className: ['!top-[24%]'] },
  ],
});

export const tailwindVariants = tv({
  slots: {
    base: [
      'bg-components-category-card-listing-color-bg',
      'flex',
      'flex-col',
      'px-themes-general-surface-spacing-small-padding-x',
      'py-themes-general-surface-spacing-small-padding-y',
      'md:py-themes-general-surface-spacing-large-padding-y',
      'md:px-themes-general-surface-spacing-large-padding-x',
    ],
    backgroundColor: [
      'fill-components-category-card-color-category-bg',
      'fill-components-category-card-color-category-bg-2',
      'fill-components-category-card-color-category-bg-3',
      'fill-components-category-card-color-category-bg-4',
      'fill-components-category-card-color-category-bg-5',
      'fill-components-category-card-color-category-bg-6',
      'fill-components-category-card-color-category-bg-7',
      'fill-components-category-card-color-category-bg-8',
      'fill-components-category-card-color-category-bg-9',
      'fill-components-category-card-color-category-bg-10',
      'fill-components-category-card-color-category-bg-11',
      'fill-components-category-card-color-category-bg-12',
      'fill-components-category-card-color-category-bg-13',
      'fill-components-category-card-color-category-bg-14',
      'fill-components-category-card-color-category-bg-15',
      'fill-components-category-card-color-category-bg-16',
      'fill-components-category-card-color-category-bg-17',
      'fill-components-category-card-color-category-bg-18',
      'fill-components-category-card-color-category-bg-19',
      'fill-components-category-card-color-category-bg-20',
    ],
    btn: [
      'hidden',
      '[&>button]:w-[24px]',
      '[&>button>div>span>svg]:!w-[20px]',
      '[&>button>div>span>svg]:!top-0',
      '[&>button]:md:group-hover:bg-components-button-color-filled-brand-pressed-bg',
      '[&>button]:md:group-hover:border-components-button-color-filled-brand-pressed-bg',
      'md:block',
    ],
    card: [
      'bg-components-category-card-color-bg',
      'border-2',
      'border-components-category-card-color-stroke',
      'border-solid',
      'flex',
      'flex-col',
      'gap-0',
      'group',
      'items-center',
      'justify-start',
      'overflow-hidden',
      'relative',
      'rounded-themes-spacing-radius-small-card',
      'md:rounded-themes-spacing-radius-large-card',
      'md:hover:border-components-category-card-color-stroke-hover',
    ],
    cardLinkWrapper: ['flex', 'flex-1', 'flex-col', 'w-full'],
    cardContainer: [
      'flex',
      'flex-row',
      'flex-wrap',
      'gap-themes-general-surface-spacing-large-cards-space-between',
      'h-full',
      'items-center',
      'items-stretch',
      'justify-start',
      'relative',
      'self-stretch',
    ],
    cardSvgWrapper: ['flex', 'w-full', 'flex-1'],
    cardContentContainer: [
      'flex',
      'flex-1',
      'flex-col',
      'items-center',
      'items-stretch',
      'justify-start',
      'md:gap-themes-general-card-spacing-large-title-margin-bottom',
      'relative',
    ],
    cardContentWrapper: [
      'bg-components-category-card-color-content-bg',
      'flex-row',
      'flex',
      'gap-spacing-padding-large-3',
      'items-center',
      'justify-start',
      'p-spacing-spacing-4',
      'relative',
      'self-stretch',
      'w-full',
      'z-10',
      'md:p-spacing-spacing-5',
    ],
    cardDescription: [
      'font-bodySans-small',
      'leading-bodySans-small',
      'text-bodySans-small',
      'line-clamp-4',
      'md:line-clamp-1',
      // line-clamp conflicts with the Capsize leading trim
      // these 2 classes resolve the issue and can be removed
      // when leading-trim gets implemented in browser spec
      // https://caniuse.com/?search=leading-trim
      'mb-[-4px]',
      'pb-[4px]',
      'border-b-[4px]',
      'border-transparent',
      'text-components-category-card-color-copy',
      'relative',
      'self-stretch',
    ],
    cardMediaWrapper: [
      'aspect-picture',
      'duration-500',
      'flex-row',
      'flex',
      'gap-2',
      'items-center',
      'justify-center',
      'm-2',
      'overflow-hidden',
      'relative',
      'scale-100',
      'self-stretch',
      'transition',
      'z-10',
      'md:group-hover:duration-500',
      'md:group-hover:scale-105',
      'md:group-hover:transition',
      'md:m-8',
    ],
    cardText: [],
    cardTitle: [
      'flex-1',
      'font-header-small-xxSmall',
      'leading-header-small-xxSmall',
      'text-header-small-xxSmall',
      'md:font-header-large-xSmall',
      'md:leading-header-large-xSmall',
      'md:text-header-large-xSmall',
      'relative',
      'self-stretch',
      'text-components-category-card-color-title',
      'tracking-cardSmall',
      'md:tracking-cardLarge',
    ],
    ctaContainer: ['flex', 'md:items-center'],
    headerContainer: [
      'flex',
      'flex-col',
      'md:flex-row',
      'md:gap-themes-general-surface-spacing-large-body-margin-right',
      'justify-between',
      'w-full',
      'mb-themes-general-surface-spacing-small-margin-bottom',
      'md:mb-themes-general-surface-spacing-large-margin-bottom',
    ],
    headerTitleContainer: ['flex', 'flex-col'],
    headerTitleText: [
      'font-header-small-large',
      'leading-header-small-large',
      'text-header-small-large',
      'md:font-header-large-large',
      'md:leading-header-large-large',
      'md:text-header-large-large',
      'text-components-category-card-listing-color-title',
    ],
    headerDescriptionText: [
      'font-bodySans-small',
      'leading-bodySans-small',
      'text-bodySans-small',
      'md:font-bodySans-medium',
      'md:leading-bodySans-medium',
      'md:text-bodySans-medium',
      'max-md:mb-themes-general-surface-spacing-small-body-margin-bottom',
      'mt-themes-general-surface-spacing-small-title-margin-bottom',
      'md:mt-themes-general-surface-spacing-large-title-margin-bottom',
      'text-components-category-card-listing-color-copy',
    ],
    legalDisclaimerText: [
      'mt-spacing-spacing-5',
      'self-start',
      '!text-components-category-card-listing-color-copy',
    ],
    media: [],
    svgLarge: ['hidden', 'md:block'],
    svgSmall: ['block', 'md:hidden'],
    title: [
      'font-header-small-large',
      'leading-header-small-large',
      'text-header-small-large',
      'tracking-header-small-large',
      'md:font-header-large-large',
      'md:leading-header-large-large',
      'md:text-header-large-large',
      'md:tracking-header-large-large',
      'text-components-category-card-listing-color-title',
    ],
    titleContainer: [
      'align-center',
      'flex',
      'flex-col',
      'items-stretch',
      'md:gap-themes-general-surface-spacing-large-body-margin-bottom',
      'md:pb-themes-general-surface-spacing-large-margin-bottom',
    ],
  },
  variants: {
    alignCTA: {
      BottomCenter: {
        ctaContainer: [
          'align-center',
          'flex',
          'justify-center',
          'w-full',
          '[&>a]:w-full',
          'md:mt-themes-general-surface-spacing-large-button-margin-top',
          'mt-themes-general-surface-spacing-small-button-margin-top',
          'md:[&>a]:w-auto',
        ],
        headerTitleContainer: ['text-center', 'w-full'],
      },
      TopRight: {
        ctaContainer: [],
      },
    },
    grid: {
      '2column': {
        card: ['w-[calc(theme(width.1/2)-12px)]'],
        cardMediaWrapper: ['lg:min-h-[480px]'],
      },
      '3column': {
        card: ['w-[calc(theme(width.1/2)-12px)]', 'md:w-[calc(theme(width.1/3)-18px)]'],
      },
      '4column': {
        card: ['w-[calc(theme(width.1/2)-12px)]', 'md:w-[calc(theme(width.1/4)-18px)]'],
      },
    },
    showDescriptionInMobile: {
      true: {
        cardContentContainer: ['max-md:gap-spacing-spacing-3'],
      },
      false: {
        cardText: ['hidden', 'md:block'],
        cardTitle: ['text-center', 'md:text-left'],
      },
    },
  },
  compoundVariants: [
    {
      brand: 'WISCJ',
      grid: '2column',
      className: {
        cardMediaWrapper: ['[&_img]:!min-h-full'],
      },
    },
  ],
});
